import type { Role } from "@/lib/types"

/**
 * Quien ve que seccion.
 *
 * ESTO NO ES CONTROL DE ACCESO. El control esta en el backend: cada endpoint
 * llama a `exigir(principal, capacidad, tenant)` y responde 403, y RLS impide
 * que una consulta cruce de tenant aunque el 403 fallara. Esconder un enlace no
 * protege nada — protege la ATENCION de quien mira: un contador no necesita seis
 * pantallas de gestion en su menu, y un CFO no necesita la cola de revision.
 *
 * Por eso la lista es de ROLES y no de capacidades: la capacidad dice que puede
 * hacer alguien; el rol dice a que se dedica, que es lo que decide si una
 * seccion le sirve.
 */
export type Seccion =
  | "dashboard"
  | "finanzas"
  | "contabilidad"
  | "holding"
  | "auditoria"

const ACCESO: Record<Seccion, Role[]> = {
  // El dashboard es de solo lectura y cruza las dos mitades: lo ve todo el
  // mundo, incluido el auditor externo.
  dashboard: ["platform_admin", "accountant", "cfo", "client_management",
              "client_finance", "viewer", "external_auditor"],

  // El modelo de gestion. El contador NO entra por defecto: su trabajo es el
  // libro, y un P&L de gestion con forecast no es una fuente contable.
  finanzas: ["platform_admin", "cfo", "client_management", "client_finance",
             "viewer", "external_auditor"],

  // La operacion contable. El CFO no entra por defecto: la cola de revision y
  // los maestros son trabajo del contador.
  contabilidad: ["platform_admin", "accountant", "client_management",
                 "viewer", "external_auditor"],

  // EL GRUPO. Un consolidado es un entregable de direccion y de auditoria, no
  // material de trabajo: lo miran quien dirige, quien audita y quien firma.
  // `client_finance` —el analista de finanzas del cliente— NO entra por
  // defecto: ve su sociedad, no el perimetro entero del grupo.
  //
  // NO pide alcance de tenant, y eso es un cambio. Lo pedia de hecho —sin que
  // nada lo dijera— porque los endpoints de `/consolidacion` exigian `read`
  // sobre el tenant: quien estaba acotado a una sociedad veia la entrada del
  // menu y al entrar recibia 403. Una puerta que no abre. Se arreglo ABRIENDO
  // LA PUERTA, no escondiendola: el backend ahora devuelve el total del grupo
  // con el detalle acotado (`consolidation/alcance.py`), que es lo que un grupo
  // real necesita — el contador de Mexico mira el total y su propia sociedad.
  holding: ["platform_admin", "cfo", "accountant", "client_management",
            "viewer", "external_auditor"],

  // LA PISTA DE AUDITORIA NO SE ABRE CON PERMISO DE LECTURA.
  //
  // Contiene los valores ANTERIORES de todo lo que se edito, que es mas de lo
  // que da un `read`: alguien que puede ver las facturas no tiene por que poder
  // ver que decian antes de que alguien las corrigiera. Los tres roles son los
  // mismos de `ROLES_CON_AUDITORIA` en el backend —y el backend es quien manda:
  // esto solo decide si el enlace aparece en el menu.
  auditoria: ["platform_admin", "accountant", "external_auditor"],
}

/**
 * Lo que la persona ALCANZA, que no es lo mismo que lo que HACE.
 *
 * El rol dice a que se dedica; el alcance, sobre que. Son dos ejes distintos y
 * el menu necesitaba los dos: un contador acotado a la sociedad de Mexico es
 * contador —el rol no cambia— pero hay pantallas que el backend no le va a
 * abrir por mucho rol que tenga.
 */
export interface Alcance {
  /**
   * Hay al menos una asignacion SIN entidad, es decir sobre el tenant entero.
   * `role_assignment.entity_id = NULL`. El admin de plataforma siempre la tiene.
   */
  tenantWide: boolean
}

/**
 * Secciones que el backend solo abre con alcance de TENANT.
 *
 * Solo esta la pista de auditoria, y por un motivo concreto: `ve_auditoria()`
 * en `core/rbac.py` comprueba `cubre(tenant_id, None)`, asi que una asignacion
 * acotada a una sociedad da `false` y los tres endpoints de `/auditoria`
 * responden 403. Sin esto, un contador acotado a Mexico ve «Auditoria» en el
 * menu y al entrar no ve nada — exactamente la puerta inventada que el Holding
 * tenia y ya no tiene.
 *
 * Y NO se arregla como el Holding, abriendo la puerta a medias: el audit log
 * guarda los valores ANTERIORES de todo lo que se edito. Un log filtrado por
 * sociedad seguiria mostrando ediciones de maestros y de configuracion que son
 * del tenant, y decidir cual es de quien es justo lo que nadie ha modelado.
 * Mientras eso no exista, la respuesta honesta es que la seccion no aparezca.
 */
const EXIGEN_TENANT: ReadonlySet<Seccion> = new Set<Seccion>(["auditoria"])

/**
 * SE MIRAN TODOS LOS ROLES, no solo el activo.
 *
 * Una persona puede tener varios roles a la vez —el mismo usuario es CFO y
 * contador— y `activeMembership` devuelve UNO, el de la sociedad activa. Gatear
 * por ese solo escondia Contabilidad a alguien que si es contador, nada mas
 * porque su primera membresia era la de CFO. El sintoma era peor que la causa:
 * la ruta redirigia al dashboard sin decir nada, como si la pantalla no
 * existiera.
 *
 * La regla es "si ALGUNO de tus roles usa esta seccion, la ves". Quitar acceso
 * de verdad se hace quitando el rol, que es donde se decide.
 */
export function puedeVer(
  seccion: Seccion,
  roles: Role | Role[] | null,
  alcance?: Alcance,
): boolean {
  if (!roles) return false
  const lista = Array.isArray(roles) ? roles : [roles]
  if (!lista.some((r) => ACCESO[seccion].includes(r))) return false

  // SIN `alcance` no se gatea por alcance. No saber que alcance tiene alguien
  // no es saber que no tiene ninguno —mismo criterio que con los roles en
  // `RequireSeccion`—: se deja pasar y responde el backend, que es el control
  // de verdad. Quien llama desde la SPA siempre lo pasa; el parametro es
  // opcional para no obligar a los tests de rol a inventarse un alcance.
  if (!alcance) return true
  return alcance.tenantWide || !EXIGEN_TENANT.has(seccion)
}

/** Las secciones visibles, en orden de menu. */
export function seccionesDe(
  roles: Role | Role[] | null,
  alcance?: Alcance,
): Seccion[] {
  return (["dashboard", "finanzas", "contabilidad", "holding", "auditoria"] as Seccion[])
    .filter((s) => puedeVer(s, roles, alcance))
}

/**
 * Quien puede ver las DIFERENCIAS entre finanzas y contabilidad.
 *
 * Solo tiene sentido para quien ve las dos mitades: a quien solo ve una, una
 * conciliacion le muestra la mitad de una resta.
 */
export function puedeVerDiferencias(roles: Role | Role[] | null): boolean {
  return puedeVer("finanzas", roles) && puedeVer("contabilidad", roles)
}
